import { z } from "zod";

import { APPLICATION_ROLES, COGNITO_ALL_PROFILE_GROUPS, defineAbilityFor } from "@recobra/core";

import { MutationProvenanceSchema } from "../automation-signals";
import {
  NotificationDisplayParamsSchema,
  NotificationLinkSchema,
  NotificationActionSchema,
} from "./notification-content";

const NotificationKind = z.enum([
  "task.assigned",
  "task.completed",
  "task.canceled",
  "task.reopened",
  "task.sla_breach",
  "task.reminder",
  "note.added",
  "note.mention",
  "case.step_changed",
  "message.received",
  "system.broadcast",
  "document.classified",
  "document.uploaded",
  "security.sign_in",
]);
export type NotificationKind = z.infer<typeof NotificationKind>;

const NotificationCategory = z.enum(["mention", "task", "system", "conversation", "security"]);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

const NotificationFilter = z.enum(["all", "unread", "mention", "system"]);
export type NotificationFilter = z.infer<typeof NotificationFilter>;

export type NotificationDisplayParams = z.infer<typeof NotificationDisplayParamsSchema>;

export type NotificationLink = z.infer<typeof NotificationLinkSchema>;

const NotificationActorSchema = z.object({
  userId: z.string().optional().meta({ visibleTo: "*" }),
  displayName: z.string().optional().meta({ visibleTo: "*" }),
  source: z.enum(["user", "system", "automation"]).optional().meta({ visibleTo: "*" }),
});

const NotificationContextSchema = z.object({
  type: z.enum(["case", "client", "task"]),
  id: z.string().min(1),
});

const NotificationUserAudienceSchema = z.object({
  type: z.literal("user"),
  userId: z.string().min(1),
});

const NotificationAudienceSchema = z.discriminatedUnion("type", [
  NotificationUserAudienceSchema,
  z.object({
    type: z.literal("broadcast"),
    scope: z.enum(["all", "internal", "groups"]),
    groupNames: z.array(z.string()).optional(),
  }),
]);
export type NotificationAudience = z.infer<typeof NotificationAudienceSchema>;

/** Only ever a create/broadcast response body, so writers see what they just wrote. */
const notificationWriters = APPLICATION_ROLES.filter((group) =>
  defineAbilityFor({ groups: [group] }).can("create", "Notification"),
);

export const NotificationRecordSchema = z.object({
  id: z.uuid().meta({ visibleTo: "*" }),
  kind: NotificationKind.meta({ visibleTo: "*" }),
  category: NotificationCategory.meta({ visibleTo: "*" }),
  title: z.string().min(1).meta({ visibleTo: "*" }),
  body: z.string().min(1).meta({ visibleTo: "*" }),
  bodyExpanded: z.string().optional().meta({ visibleTo: "*" }),
  action: NotificationActionSchema.optional().meta({ visibleTo: "*" }),
  link: NotificationLinkSchema.meta({ visibleTo: "*" }),
  actor: NotificationActorSchema.optional().meta({ visibleTo: notificationWriters }),
  context: NotificationContextSchema.optional().meta({ visibleTo: notificationWriters }),
  audience: NotificationAudienceSchema.meta({ visibleTo: notificationWriters }),
  createdAt: z.iso.datetime().meta({ visibleTo: "*" }),
  expiresAt: z.iso.datetime().optional().meta({ visibleTo: "*" }),
  dedupeKey: z.string().optional().meta({ visibleTo: notificationWriters }),
  createdMutation: MutationProvenanceSchema.optional(),
  displayParams: NotificationDisplayParamsSchema.optional().meta({ visibleTo: "*" }),
});
export type NotificationRecord = z.infer<typeof NotificationRecordSchema>;

const NotificationUserStateSchema = z.object({
  notificationId: z.uuid(),
  userId: z.string().min(1),
  readAt: z.iso.datetime().nullable().optional(),
  clearedAt: z.iso
    .datetime()
    .nullable()
    .optional()
    .describe(
      "When the notification leaves the inbox. Set to readAt + retention on mark-read, or now on manual clear.",
    ),
});
export type NotificationUserState = z.infer<typeof NotificationUserStateSchema>;

export const NotificationInboxItemSchema = z.object({
  id: z.uuid().meta({ visibleTo: "*" }),
  kind: NotificationKind.meta({ visibleTo: "*" }),
  category: NotificationCategory.meta({ visibleTo: "*" }),
  title: z.string().meta({ visibleTo: "*" }),
  body: z.string().meta({ visibleTo: "*" }),
  bodyExpanded: z.string().optional().meta({ visibleTo: "*" }),
  action: NotificationActionSchema.optional().meta({ visibleTo: "*" }),
  link: NotificationLinkSchema.meta({ visibleTo: "*" }),
  actor: NotificationActorSchema.optional().meta({ visibleTo: "*" }),
  displayParams: NotificationDisplayParamsSchema.optional().meta({ visibleTo: "*" }),
  createdAt: z.iso.datetime().meta({ visibleTo: "*" }),
  readAt: z.iso.datetime().nullable().optional().meta({ visibleTo: "*" }),
  isUnread: z.boolean().meta({ visibleTo: "*" }),
});
export type NotificationInboxItem = z.infer<typeof NotificationInboxItemSchema>;

export const ListNotificationsQuerySchema = z.object({
  filter: NotificationFilter.default("all"),
});

export const ListNotificationsResponseSchema = z.object({
  items: z.array(NotificationInboxItemSchema).meta({ visibleTo: "*" }),
});
export type ListNotificationsResponse = z.infer<typeof ListNotificationsResponseSchema>;

export const NotificationSummarySchema = z.object({
  total: z.number().int().nonnegative().meta({ visibleTo: "*" }),
  unread: z.number().int().nonnegative().meta({ visibleTo: "*" }),
  read: z.number().int().nonnegative().meta({ visibleTo: "*" }),
  byCategory: z
    .object({
      mention: z.number().int().nonnegative().meta({ visibleTo: "*" }),
      system: z.number().int().nonnegative().meta({ visibleTo: "*" }),
    })
    .meta({ visibleTo: "*" }),
});
export type NotificationSummary = z.infer<typeof NotificationSummarySchema>;

export const CreateBroadcastNotificationSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(4000),
    bodyExpanded: z.string().trim().max(8000).optional(),
    scope: z
      .enum(["all", "internal", "groups"])
      .describe(
        "Who receives it; stated explicitly so an omission cannot leak an internal announcement",
      ),
    groupNames: z
      .array(z.enum(COGNITO_ALL_PROFILE_GROUPS))
      .min(1)
      .optional()
      .describe("Required when scope is 'groups'; the exact Cognito groups that receive it"),
    dedupeKey: z.string().optional().describe("Idempotency key — required for automation retries"),
    action: NotificationActionSchema.optional(),
    link: NotificationLinkSchema.optional(),
    expiresAt: z.iso.datetime().optional(),
  })
  .refine((input) => input.scope !== "groups" || (input.groupNames?.length ?? 0) > 0, {
    message: "groupNames is required when scope is 'groups'",
    path: ["groupNames"],
  });
export type CreateBroadcastNotificationInput = z.infer<typeof CreateBroadcastNotificationSchema>;

const CreateNotificationBaseSchema = NotificationRecordSchema.omit({
  id: true,
  createdAt: true,
  audience: true,
}).extend({
  id: z.uuid().optional(),
});

const CreateNotificationInputSchema = CreateNotificationBaseSchema.extend({
  audience: NotificationAudienceSchema,
});

export type CreateNotificationInput = z.input<typeof CreateNotificationInputSchema>;

/** Automation body — may target a user by Cognito username instead of sub. */
export const CreateAutomationNotificationSchema = CreateNotificationBaseSchema.extend({
  recipientUsername: z.string().min(1).optional(),
  audience: NotificationUserAudienceSchema.optional().describe(
    "Targeted only; broadcasting has its own endpoint and its own permission",
  ),
}).superRefine((data, ctx) => {
  if (!data.recipientUsername && !data.audience) {
    ctx.addIssue({
      code: "custom",
      message: "recipientUsername or audience is required",
      path: ["recipientUsername"],
    });
  }
});
export type CreateAutomationNotificationInput = z.input<typeof CreateAutomationNotificationSchema>;
